import React, { Fragment } from 'react';
import {
  Box,
  ChoiceBox,
  ChoiceboxGroup,
  Text,
} from '@sparknz/spark-pattern-library';
import { LinkRegion, trackCustomLink } from '@sparknz/tracking';

import { useProductDetails } from 'journey/productDetails/provider';
import { ProductDetailsActionTypes } from 'journey/productDetails/reducer/productDetails.reducer';
import { WearableEligibilityCheckSteps } from 'journey/productDetails/typings';
import ChangeAccountButton from '../../../../../AddToCartSection/components/ChangeAccountButton';
import IneligibleWarning from '../IneligibleWarning';

interface ExistingPlanProps {
  selectedLineNumber: string;
  setSelectedLineNumber: (lineNumber: string) => void;
}

const ExistingPlans = ({
  selectedLineNumber,
  setSelectedLineNumber,
}: ExistingPlanProps) => {
  const {
    wearableEligibility: {
      eligibility: { accounts, eligibleLines, ineligibleLines },
    },
    dispatch,
  } = useProductDetails();

  function handleExistingPlanSelection(lineNumber: string) {
    setSelectedLineNumber(lineNumber);
  }

  function handleChangeAccount() {
    dispatch({
      type: ProductDetailsActionTypes.SetWearableEligibility,
      wearableEligibility: {
        activeStep: WearableEligibilityCheckSteps.AccountSelection,
      },
    });
    trackCustomLink('Change account');
  }

  const lines = [
    ...eligibleLines.map((line) => ({
      ...line,
      reason: null,
      isEligible: true,
    })),
    ...ineligibleLines.map((line) => ({ ...line, isEligible: false })),
  ];

  const hasNoLines = lines.length === 0;

  if (hasNoLines) {
    return <IneligibleWarning hasLines={false} />;
  }

  return (
    <>
      <ChangeAccountButton
        accountName={lines[0].accountName}
        accountNumber={lines[0].accountNumber}
        onChangeAccount={handleChangeAccount}
        numberOfAccounts={accounts.length}
      />

      <Box mb="s3">
        <ChoiceboxGroup title="Your plans">
          {lines.map(
            ({ lineNumber, offerId, offerName, isEligible, reason }) => {
              const planKey = `${lineNumber}-${offerId}`;
              const isIneligibleLine =
                planKey === selectedLineNumber && !isEligible;
              const isChecked = planKey === selectedLineNumber;

              return (
                <Fragment key={planKey}>
                  <ChoiceBox
                    name={offerName}
                    checked={isChecked}
                    value={planKey}
                    onChange={() => {
                      handleExistingPlanSelection(planKey);
                    }}
                  >
                    <Box>
                      <LinkRegion isNotTracked>
                        <Text variant="bodySDemi">{lineNumber}</Text>
                      </LinkRegion>
                      <LinkRegion regionId="productDetails.line.yourPlans">
                        <Text variant="bodySMedium">{offerName}</Text>
                      </LinkRegion>
                    </Box>
                  </ChoiceBox>
                  {isIneligibleLine ? (
                    <IneligibleWarning reason={reason} hasLines />
                  ) : null}
                </Fragment>
              );
            },
          )}
        </ChoiceboxGroup>
      </Box>
    </>
  );
};

export default ExistingPlans;
